// apps/admin/src/components/cmdk/useCommandPaletteHotkey.tsx
//
// Phase 8.1 Wave 3 Task 14 — ⌘K / Ctrl+K hotkey hook for the Cmd+K palette.
//
// vg-binding: CONTEXT:P8.D-23
// vg-binding: CONTEXT:P8.D-29
// vg-binding: CONTEXT:P8.D-153
// vg-binding: PLAN:task-14
//
// Listens on `window` for keydown and toggles the supplied open setter when
// Cmd+K (macOS) or Ctrl+K (others) fires. Hitting the chord while the
// palette is already open closes it (toggle semantics — keyboard
// affordance). ESC is owned by the cmdk dialog itself (built-in focus
// trap); this hook handles only the open/toggle chord.

import { useEffect } from 'react';

/**
 * Wires a global ⌘K / Ctrl+K listener that toggles `onToggle`.
 *
 * The handler runs on `window` so it fires regardless of which element
 * holds focus — except when typing inside an input/textarea/contentEditable
 * surface where ⌘K may shadow a native shortcut (browser address bar on
 * Linux/Windows binds Ctrl+K to search; respecting that there is correct).
 * To keep the chord reliable everywhere inside the SPA, we do NOT skip
 * input contexts: the consumer can decide whether the palette should open
 * when an input is focused (the cmdk dialog itself steals focus on mount).
 */
export function useCommandPaletteHotkey(onToggle: () => void): void {
  useEffect(() => {
    function handler(event: KeyboardEvent): void {
      // `metaKey` is the macOS Cmd modifier; `ctrlKey` covers Linux/Windows.
      const isHotkey =
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        !event.shiftKey &&
        event.key.toLowerCase() === 'k';
      if (!isHotkey) return;

      // Prevent the browser's default Ctrl+K (search bar focus on
      // Firefox/Chrome non-mac), then defer the state update so React
      // does not batch with an unrelated input event.
      event.preventDefault();
      event.stopPropagation();
      onToggle();
    }

    window.addEventListener('keydown', handler, { capture: true });
    return () => {
      window.removeEventListener('keydown', handler, { capture: true });
    };
  }, [onToggle]);
}

export default useCommandPaletteHotkey;
